import { createEntityAdapter } from "@reduxjs/toolkit";
import { TasksApiTag } from "./tags";
import {
  DetailedTask,
  InternalTaskFilterSet,
  InternalTaskFilters,
  PreviewTask,
  TaskFilterSet,
  TaskFilters,
  TasksBoardId,
} from "./types";

export const getTaskByIdTag = (taskId: string) => ({
  type: TasksApiTag.task,
  id: `ID-${taskId}`,
});

export const getTaskByBoardIdTag = (boardId: TasksBoardId) => ({
  type: TasksApiTag.task,
  id: `BOARD-${boardId}`,
});

export const getTaskByEntityIdTag = (entityId: string) => ({
  type: TasksApiTag.task,
  id: `ENTITY-${entityId}`,
});

export const getBoardByEntityIdTag = (entityId: string) => ({
  type: TasksApiTag.board,
  id: `ENTITY-${entityId}`,
});

export const sortCollectionByOrder = <TItem extends { order: number }>(
  items: TItem[]
) => [...items].sort((itemA, itemB) => itemA.order - itemB.order);

export const adaptDetailedTaskToPreviewTask = (
  detailedTask: DetailedTask
): PreviewTask => ({
  ...detailedTask,
  // Use placeholders as in some cases backend doesn't return subtask counts
  chklist_total: detailedTask.chklist_total || 0,
  chklist_completed: detailedTask.chklist_completed || 0,
});

export const previewTasksAdapter = createEntityAdapter<PreviewTask>({
  selectId: (task) => task.id,
});

// We prefix task description with a hash because WAF doesn't allow strings starting with HTML tags
const TASK_DESCRIPTION_HASH = "_gJ15r";

export const withPrefixedTaskDescription = <
  TPayload extends Partial<DetailedTask>,
>(
  payload: TPayload
): TPayload => {
  if (!payload.desc) {
    return payload;
  }

  return { ...payload, desc: TASK_DESCRIPTION_HASH.concat(payload.desc) };
};

export const stripTaskDescriptionPrefix = (description: string) => {
  if (!description.startsWith(TASK_DESCRIPTION_HASH)) {
    return description?.startsWith("<") ? description : `<p>${description}</p>`;
  }
  return description.slice(TASK_DESCRIPTION_HASH.length);
};

export const makeCustomFieldsSafe = (
  task: Pick<PreviewTask, "custom_fields">
) => {
  if (!task.custom_fields) {
    return;
  }
  Object.keys(task.custom_fields).forEach((key) => {
    if (!task.custom_fields?.[key]) {
      return;
    }
    if (typeof task.custom_fields[key] === "object") {
      try {
        task.custom_fields[key] = JSON.stringify(task.custom_fields[key]);
      } catch {
        task.custom_fields[key] = "";
      }
    }
  });
};

export const adaptInternalTaskFiltersToTaskFilters = (
  filters: InternalTaskFilters
): TaskFilters => ({
  board: filters.boards ? [...filters.boards] : null,
  inv_item: filters.inventoryItems ? [...filters.inventoryItems] : null,
  assignee: filters.assignees ? [...filters.assignees] : null,
  customer: filters.customers ? [...filters.customers] : null,
  label: filters.labels ? [...filters.labels] : null,
  overdue: filters.isOverdue,
  recurring: filters.isRecurring,
  priority: filters.priorities ? [...filters.priorities] : null,
  status: filters.statuses ? [...filters.statuses] : null,
  custom_fields: filters.customFields
    ? Object.fromEntries(
        Object.entries(filters.customFields).map(([key, value]) => [
          key,
          [...value],
        ])
      )
    : null,
});

export const adaptTaskFiltersToInternalTaskFilters = (
  filters: TaskFilters
): InternalTaskFilters => ({
  boards: filters.board || [],
  inventoryItems: filters.inv_item || [],
  assignees: filters.assignee || [],
  customers: filters.customer || [],
  labels: filters.label || [],
  isOverdue: filters.overdue,
  isRecurring: filters.recurring,
  priorities: filters.priority || [],
  statuses: filters.status || [],
  customFields: filters.custom_fields || {},
});

export const adaptTaskFilterSetToInternalTaskFilterSet = (
  filterSet: TaskFilterSet
): InternalTaskFilterSet => ({
  id: filterSet.id,
  name: filterSet.name,
  filters: adaptTaskFiltersToInternalTaskFilters(filterSet),
  updatedAt: filterSet.updated_at,
});

export const adaptInternalTaskFilterSetToTaskFilterSet = (
  filterSet: InternalTaskFilterSet
): TaskFilterSet => ({
  id: filterSet.id,
  name: filterSet.name,
  updated_at: filterSet.updatedAt,
  ...adaptInternalTaskFiltersToTaskFilters(filterSet.filters),
});

/**
 * Filters out tasks with names set to null due to a backend issue in the past.
 * Ensures backward compatibility with the old data.
 * Should be removed after backend migrates/removes tasks without names
 */
export const filterOutInvalidTaskLikeObjects = <
  TTaskLikeObject extends { name: string } | { task_name: string },
>(
  taskLikeObjects: TTaskLikeObject[]
): TTaskLikeObject[] =>
  taskLikeObjects.filter((taskLikeObject) => {
    if ("name" in taskLikeObject) {
      return taskLikeObject.name !== null;
    }

    return taskLikeObject.task_name !== null;
  });
